/**
 * Autocomplete de empresa cliente / proveedor.
 *
 * `onlySuppliers`: filtra a empresas con is_supplier=true. Lo usa el modal de
 * gasto en Finanzas para no mezclar el CRM de clientes con los proveedores.
 *
 * UX:
 *   - Buscar por nombre o industria; resultados scrollables (sin cap)
 *   - Si no hay match exacto, botón "+ Crear …" abre un mini-form inline
 *     que crea la empresa y la auto-selecciona. Pre-rellena isSupplier
 *     según el modo del picker.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Check, AlertCircle, Plus, X } from "lucide-react";
import clsx from "clsx";
import { api } from "@/lib/api";

interface Company {
  id: string;
  name: string;
  status: "active" | "prospect" | "past" | "lost";
  industry: string | null;
  isInternal: boolean;
  isSupplier?: boolean;
}

interface CompanyPickerProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  error?: string;
  /** Solo proveedores (isSupplier=true). Para modal de gasto. */
  onlySuppliers?: boolean;
  /** Solo clientes del CRM (isSupplier=false). Para modal de ingreso. */
  onlyClients?: boolean;
}

export function CompanyPicker({
  value,
  onChange,
  error,
  onlySuppliers,
  onlyClients,
}: CompanyPickerProps) {
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createIndustry, setCreateIndustry] = useState("");

  const apiUrl = onlySuppliers
    ? "/companies?isSupplier=1"
    : onlyClients
      ? "/companies?isSupplier=0"
      : "/companies";

  const { data } = useQuery<{ companies: Company[] }>({
    queryKey: ["companies", { onlySuppliers: !!onlySuppliers, onlyClients: !!onlyClients }],
    queryFn: () => api.get(apiUrl),
    staleTime: 5 * 60_000,
  });

  const companies = useMemo(() => data?.companies ?? [], [data]);
  const selected = companies.find((c) => c.id === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = companies.filter((c) => {
      if (c.isInternal) return false;
      if (onlySuppliers && c.isSupplier !== true) return false;
      if (onlyClients && c.isSupplier === true) return false;
      return true;
    });
    if (!q) return list;
    return list.filter(
      (c) => c.name.toLowerCase().includes(q) || (c.industry ?? "").toLowerCase().includes(q),
    );
  }, [companies, query, onlySuppliers, onlyClients]);

  // ¿La query coincide EXACTAMENTE con alguna existente? Si no, ofrecemos crear.
  const exactMatch = filtered.some((c) => c.name.toLowerCase() === query.trim().toLowerCase());

  const create = useMutation<{ id: string }, Error, void>({
    mutationFn: () =>
      api.post<{ id: string }>("/companies", {
        name: query.trim(),
        industry: createIndustry.trim() || undefined,
        isSupplier: !!onlySuppliers,
        status: onlySuppliers ? "active" : "prospect",
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      onChange(res.id);
      setQuery("");
      setCreateIndustry("");
      setCreating(false);
      setOpen(false);
    },
  });

  return (
    <div className="space-y-1.5 relative">
      {selected ? (
        <div
          className={clsx(
            "card-elevated p-3 flex items-center justify-between gap-3",
            error && "border-error",
          )}
        >
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{selected.name}</div>
            <div className="text-xs text-base-content/55 truncate">
              {selected.industry ?? "—"}
              {!onlySuppliers && ` · ${selected.status}`}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              onChange(undefined);
              setQuery("");
            }}
            className="btn btn-ghost btn-xs"
          >
            Cambiar
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => {
                // Delay para permitir clicks en el dropdown. Si está abierto el
                // mini-form de crear, NO cerramos.
                setTimeout(() => {
                  if (!creating) setOpen(false);
                }, 150);
              }}
              placeholder="Buscar empresa por nombre"
              className={clsx("input input-bordered w-full pl-9", error && "input-error")}
            />
          </div>
          {open && (
            <div className="absolute left-0 right-0 top-full mt-1 z-30 card-elevated max-h-72 overflow-y-auto p-1">
              {filtered.length === 0 && !query && (
                <div className="px-3 py-3 text-sm text-base-content/55 italic">
                  Sin empresas. Escribe un nombre para crear la primera.
                </div>
              )}
              {filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange(c.id);
                    setQuery("");
                    setOpen(false);
                  }}
                  className="w-full px-3 py-2 text-left rounded-md hover:bg-base-200 transition-colors flex items-center justify-between gap-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-xs text-base-content/55 truncate">{c.industry ?? "—"}</div>
                  </div>
                  {!onlySuppliers && (
                    <span className="badge badge-ghost badge-sm capitalize shrink-0">
                      {c.status}
                    </span>
                  )}
                  {c.id === value && <Check className="size-3.5 text-primary" />}
                </button>
              ))}

              {/* Crear inline si el nombre buscado no existe exacto */}
              {query.trim().length >= 2 && !exactMatch && (
                <div className="border-t border-base-300/60 mt-1 pt-1">
                  {!creating ? (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setCreating(true)}
                      className="w-full px-3 py-2 text-left rounded-md hover:bg-primary/10 transition-colors flex items-center gap-2 text-primary"
                    >
                      <Plus className="size-4 shrink-0" />
                      <span className="text-sm">
                        Crear <strong>"{query.trim()}"</strong> como{" "}
                        {onlySuppliers ? "proveedor" : onlyClients ? "cliente" : "empresa"}
                      </span>
                    </button>
                  ) : (
                    <div className="px-3 py-2 space-y-2" onMouseDown={(e) => e.preventDefault()}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs font-semibold uppercase tracking-wider text-base-content/65">
                          Crear "{query.trim()}"
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setCreating(false);
                            setCreateIndustry("");
                          }}
                          className="btn btn-ghost btn-xs btn-square"
                        >
                          <X className="size-3" />
                        </button>
                      </div>
                      <input
                        type="text"
                        autoFocus
                        value={createIndustry}
                        onChange={(e) => setCreateIndustry(e.target.value)}
                        placeholder="Industria (opcional) — ej. SaaS, Banca, Servicios"
                        className="input input-bordered input-sm w-full"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            create.mutate();
                          } else if (e.key === "Escape") {
                            setCreating(false);
                          }
                        }}
                      />
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setCreating(false);
                            setCreateIndustry("");
                          }}
                          className="btn btn-ghost btn-xs"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={() => create.mutate()}
                          disabled={create.isPending}
                          className="btn btn-primary btn-xs"
                        >
                          {create.isPending && (
                            <span className="loading loading-spinner loading-xs" />
                          )}
                          Crear y seleccionar
                        </button>
                      </div>
                      {create.error && (
                        <div className="text-xs text-error">{create.error.message}</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
      {error && (
        <span className="text-xs text-error mt-1 flex items-center gap-1">
          <AlertCircle className="size-3" /> {error}
        </span>
      )}
    </div>
  );
}
